import {
    GridView as GridViewIcon,
    List as ListViewIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import {
    Box,
    Chip,
    Grid,
    InputAdornment,
    Paper,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import React, { useState } from "react";
import { FileItem } from "../Api";
import FileCard from "../components/FileCard";
import FileList, { SortDirection, SortField } from "../components/FileList";

type ViewMode = 'grid' | 'list';

// Mock data for demonstration
const mockFiles: FileItem[] = [
    {
        key: "family-vacation-2023.mp4",
        filename: "family-vacation-2023.mp4",
        title: "Family Vacation 2023",
        date: "2023-07-15",
        size: "524288000", // 500MB
        upload: "1690300800000", // July 25, 2023
        lastModified: "1690300800000",
        signedUrl: "https://example.com/video1"
    },
    {
        key: "wedding-photos.jpg",
        filename: "wedding-photos.jpg",
        title: "Wedding Photos Collection",
        date: "2022-09-10",
        size: "15728640", // 15MB
        upload: "1663200000000", // Sept 15, 2022
        lastModified: "1663200000000",
        signedUrl: "https://example.com/photo1"
    },
    {
        key: "birthday-party.mov",
        filename: "birthday-party.mov",
        title: "Birthday Party Video",
        date: "2023-03-22",
        size: "314572800", // 300MB
        upload: "1679500800000", // March 22, 2023
        lastModified: "1679500800000",
        signedUrl: undefined
    },
    {
        key: "family-recipe.pdf",
        filename: "family-recipe.pdf",
        title: "Grandma's Secret Recipe",
        date: "2020-12-25",
        size: "2097152", // 2MB
        upload: "1608940800000", // Dec 25, 2020
        lastModified: "1608940800000",
        signedUrl: "https://example.com/doc1"
    },
    {
        key: "graduation-ceremony.mp4",
        filename: "graduation-ceremony.mp4",
        title: "Graduation Ceremony 2023",
        date: "2023-06-15",
        size: "1073741824", // 1GB
        upload: "1686844800000", // June 15, 2023
        lastModified: "1686844800000",
        signedUrl: "https://example.com/video2"
    },
    {
        key: "baby-photos.png",
        filename: "baby-photos.png",
        title: "Baby's First Steps",
        date: "2023-01-10",
        size: "5242880", // 5MB
        upload: "1673366400000", // Jan 10, 2023
        lastModified: "1673366400000",
        signedUrl: "https://example.com/photo2"
    },
    {
        key: "christmas-dinner.jpg",
        filename: "christmas-dinner.jpg",
        title: "Christmas Dinner 2022",
        date: "2022-12-25",
        size: "8388608", // 8MB
        upload: "1671926400000", // Dec 25, 2022
        lastModified: "1671926400000",
        signedUrl: "https://example.com/photo3"
    },
    {
        key: "school-report.docx",
        filename: "school-report.docx",
        title: "Annual School Report",
        date: "2023-05-30",
        size: "1048576", // 1MB
        upload: "1685404800000", // May 30, 2023
        lastModified: "1685404800000",
        signedUrl: undefined
    }
];

export const FileListDemo: React.FC = () => {
    const [viewMode, setViewMode] = useState<ViewMode>("list");
    const [searchTerm, setSearchTerm] = useState("");
    const [sortField, setSortField] = useState<SortField>("date");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    function startTranscode(fileItem: FileItem) {
        alert(`Transcode requested for: ${fileItem.title || fileItem.filename}`);
    }

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            // Toggle direction if same field
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // New field, default to ascending
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const sortedItems = React.useMemo(() => {
        // Filter items based on search term
        const filteredItems = mockFiles.filter(item => {
            const searchLower = searchTerm.toLowerCase();
            const title = (item.title || item.filename).toLowerCase();
            const filename = item.filename.toLowerCase();
            return title.includes(searchLower) || filename.includes(searchLower);
        });

        // Sort items based on selected criteria
        return [...filteredItems].sort((a, b) => {
            let comparison = 0;
            
            switch (sortField) {
                case "name":
                    comparison = (a.title || a.filename).localeCompare(b.title || b.filename);
                    break;
                case "size":
                    comparison = parseInt(a.size) - parseInt(b.size);
                    break;
                case "upload":
                    comparison = parseInt(a.upload) - parseInt(b.upload);
                    break;
                case "type":
                    const extA = a.filename.toLowerCase().split('.').pop() || '';
                    const extB = b.filename.toLowerCase().split('.').pop() || '';
                    comparison = extA.localeCompare(extB);
                    break;
                case "date":
                default:
                    if (a.date && b.date) {
                        comparison = a.date.localeCompare(b.date);
                    } else if (a.date) {
                        comparison = 1;
                    } else if (b.date) {
                        comparison = -1;
                    } else {
                        comparison = a.upload.localeCompare(b.upload);
                    }
                    break;
            }
            
            return sortDirection === 'asc' ? comparison : -comparison;
        });
    }, [searchTerm, sortField, sortDirection]);

    const getFileTypeStats = () => {
        const stats: { [key: string]: number } = {};
        mockFiles.forEach(item => {
            const extension = item.filename.toLowerCase().split('.').pop() || 'unknown';
            stats[extension] = (stats[extension] || 0) + 1;
        });
        return stats;
    };

    const fileTypeStats = getFileTypeStats();

    return (
        <Box sx={{ p: 3 }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom>
                    Enhanced File List Demo
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Demonstration of the new list view with sortable columns and enhanced controls
                </Typography>

                {/* Stats */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Collection Overview
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip label={`Total Files: ${mockFiles.length}`} color="primary" />
                        {Object.entries(fileTypeStats).map(([type, count]) => (
                            <Chip
                                key={type}
                                label={`${type.toUpperCase()}: ${count}`}
                                variant="outlined"
                                size="small"
                            />
                        ))}
                    </Box>
                    <Chip label="Demo Mode" color="info" size="small" />
                </Paper>

                {/* Search and View Controls */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        placeholder="Search files..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ flexGrow: 1, minWidth: 250 }}
                    />
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(_, newMode) => newMode && setViewMode(newMode)}
                        size="small"
                    >
                        <ToggleButton value="list" aria-label="list view">
                            <ListViewIcon />
                        </ToggleButton>
                        <ToggleButton value="grid" aria-label="grid view">
                            <GridViewIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {/* Feature Highlights */}
                <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
                    <Typography variant="h6" gutterBottom color="primary">
                        ✨ New Features Demonstrated:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip label="📋 List View" color="primary" variant="outlined" />
                        <Chip label="🔄 Sortable Columns" color="primary" variant="outlined" />
                        <Chip label="🔍 Enhanced Search" color="primary" variant="outlined" />
                        <Chip label="📱 Responsive Design" color="primary" variant="outlined" />
                        <Chip label="⚡ Better Performance" color="primary" variant="outlined" />
                    </Box>
                </Paper>
            </Box>

            {/* Files Display */}
            {sortedItems.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {searchTerm ? 'No files match your search' : 'No files found'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {searchTerm ? 'Try adjusting your search terms' : 'Upload some files to get started'}
                    </Typography>
                </Paper>
            ) : viewMode === 'list' ? (
                <FileList
                    files={sortedItems}
                    onTranscode={startTranscode}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                />
            ) : (
                <Grid container spacing={3}>
                    {sortedItems.map((fileItem) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={fileItem.key}>
                            <FileCard
                                fileItem={fileItem}
                                onTranscode={startTranscode}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
}

export default FileListDemo;
